import { useRef } from 'react'
import { Link } from 'react-router-dom'
import ForceHeader from '../components/ForceHeader'
import ForceFooter from '../components/ForceFooter'
import ForceHeroCard from '../components/ForceHeroCard'
import AskAnythingBar from '../components/AskAnythingBar'

// An original decorative wreath badge (no national/force emblem, just a generic laurel
// ring) so the module buttons can have an "official-looking" medallion without copying
// any real government or paramilitary insignia.
function WreathBadge({ icon, ring, leaf, bg }: { icon: string; ring: string; leaf: string; bg: string }) {
  const leafAngles = [100, 118, 136, 154, 172]
  const leaf2 = (deg: number) => 180 - deg
  const point = (deg: number, r: number) => {
    const rad = (deg * Math.PI) / 180
    return { x: 24 + r * Math.cos(rad), y: 24 + r * Math.sin(rad) }
  }
  return (
    <svg viewBox="0 0 48 48" className="w-9 h-9 shrink-0" aria-hidden="true">
      <circle cx="24" cy="24" r="21" fill={bg} stroke={ring} strokeWidth="1.5" />
      {leafAngles.map((deg) => {
        const { x, y } = point(deg, 16.5)
        return <ellipse key={`l${deg}`} cx={x} cy={y} rx="2.4" ry="5" fill={leaf} transform={`rotate(${deg - 90} ${x} ${y})`} />
      })}
      {leafAngles.map((deg) => {
        const m = leaf2(deg)
        const { x, y } = point(m, 16.5)
        return <ellipse key={`r${deg}`} cx={x} cy={y} rx="2.4" ry="5" fill={leaf} transform={`rotate(${m - 90} ${x} ${y})`} />
      })}
      <circle cx="24" cy="24" r="10.5" fill="none" stroke={ring} strokeWidth="1" opacity="0.6" />
      <foreignObject x="10" y="10" width="28" height="28">
        <div style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span className="material-symbols-outlined" style={{ fontSize: 18, color: ring, lineHeight: 1 }}>
            {icon}
          </span>
        </div>
      </foreignObject>
    </svg>
  )
}

function CornerBracket({ className }: { className: string }) {
  return <div className={`absolute w-3.5 h-3.5 border-force-green-light/35 pointer-events-none ${className}`} />
}

function CardBrackets() {
  return (
    <>
      <CornerBracket className="top-2 left-2 border-t border-l" />
      <CornerBracket className="top-2 right-2 border-t border-r" />
      <CornerBracket className="bottom-2 left-2 border-b border-l" />
      <CornerBracket className="bottom-2 right-2 border-b border-r" />
    </>
  )
}

const WHY_FORCE_AI = [
  {
    icon: 'schedule',
    title: 'Time management',
    body: 'Everything is well organized here. Just attempt the test.',
  },
  {
    icon: 'bolt',
    title: 'No long waiting for results',
    body: "Get immediate results and AI Assessor's verdict.",
  },
  {
    icon: 'public',
    title: 'Take the test anytime, anywhere',
    body: 'You have the flexibility to practice on your own terms.',
  },
  {
    icon: 'forum',
    title: 'More than just tests',
    body: 'Get a personal AI touch. Ask anything, get answers and follow-up support in your SSB preparation journey.',
  },
  {
    icon: 'map',
    title: 'Force AI gives you a clear blueprint or roadmap',
    body: 'It shows what to do, how many tests to attempt each day, and how to improve. So your recommendation chances increase.',
  },
]

const HOW_IT_WORKS = [
  { step: '01', title: 'Submit your PIQ', body: 'Your Personal Information Questionnaire becomes the baseline every later answer is checked against.' },
  { step: '02', title: 'Take timed test', body: 'Full length timed test and practice test, followed by AI simulated real-time assessors review.' },
  { step: '03', title: 'Get your report', body: 'AI scoring across all eight Officer-Like Qualities, with specialized assessor review and feedback.' },
  { step: '04', title: 'Practice daily', body: 'Track readiness on your dashboard and return to your weakest areas before your next attempt.' },
]

const FEATURES = [
  { icon: 'psychology', title: 'Force AI and a psych analysis', body: 'In-depth analysis of all psych test with assessor verdict and review setting the benchmark.' },
  { icon: 'record_voice_over', title: 'Interview Simulator', body: 'Practice a personal-interview-style Q&A and get feedback on both content and delivery.' },
  { icon: 'monitoring', title: 'Performance Dashboard', body: 'Track improvement across every checkpoint, from your first PIQ draft to your latest report.' },
  { icon: 'target', title: 'Tailored Feedback', body: 'Per-answer interpretation and reframes, not just a single aggregate score.' },
  { icon: 'calendar_month', title: 'Daily Practice Plan', body: 'A structured routine that keeps you consistent between attempts.' },
  { icon: 'military_tech', title: 'One True Assessor', body: 'Backed by DIPR approved assessors and recommended candidates data, one true assessor with multiple verified sources.' },
]

const RECOMMENDED_CANDIDATES = [
  { name: 'Ayush Agarwal', photo: '/assets/recommended/ayush-agarwal.jpg' },
  { name: 'Shubhangi Nair', photo: '/assets/recommended/shubhangi-nair.jpg' },
  { name: 'Vatsala Mathur', photo: '/assets/recommended/vatsala-mathur.jpg' },
  { name: 'Sachin Pal', photo: '/assets/recommended/sachin-pal.jpg' },
  { name: 'Rahul', photo: '/assets/recommended/rahul.jpg' },
  { name: 'Keshav Kumar', photo: '/assets/recommended/keshav-kumar.jpg' },
  { name: 'Jai Chouhan', photo: '/assets/recommended/jai-chouhan.jpg' },
  { name: 'Priyanshu Pandey', photo: '/assets/recommended/priyanshu-pandey.jpg' },
  { name: 'Shubham Kumar', photo: '/assets/recommended/shubham-kumar.jpg' },
  { name: 'Ashika Shetty', photo: '/assets/recommended/ashika-shetty.jpg' },
  { name: 'Bhushan More', photo: '/assets/recommended/bhushan-more.jpg' },
  { name: 'Rohit Kumar', photo: '/assets/recommended/rohit-kumar.jpg' },
  { name: 'Anuj Deshmukh', photo: '/assets/recommended/anuj-deshmukh.jpg' },
  { name: 'Vishal Pratap Shahi', photo: '/assets/recommended/vishal-pratap-shahi.jpg' },
  { name: 'Abhijeet Singh', photo: '/assets/recommended/abhijeet-singh.jpg' },
  { name: 'Harsh Pratap Singh', photo: '/assets/recommended/harsh-pratap-singh.jpg' },
  { name: 'Rahul Dogra', photo: '/assets/recommended/rahul-dogra.jpg' },
  { name: 'Anmol', photo: '/assets/recommended/anmol.jpg' },
  { name: 'Bharat', photo: '/assets/recommended/bharat.jpg' },
  { name: 'Vishal Chaudhary', photo: '/assets/recommended/vishal-chaudhary.jpg' },
  { name: 'Sachin Sharma', photo: '/assets/recommended/sachin-sharma.jpg' },
  { name: 'Varsha Hiraskar', photo: '/assets/recommended/varsha-hiraskar.jpg' },
  { name: 'Sushant Yadav', photo: '/assets/recommended/sushant-yadav.jpg' },
]

function RecommendationCarousel({ items }: { items: { name: string; photo: string }[] }) {
  const scrollerRef = useRef<HTMLDivElement>(null)

  const scrollByCards = (dir: number) => {
    scrollerRef.current?.scrollBy({ left: dir * 280, behavior: 'smooth' })
  }

  return (
    <div className="relative flex items-center gap-2">
      <button
        type="button"
        onClick={() => scrollByCards(-1)}
        aria-label="Scroll recommendations left"
        className="hidden sm:flex shrink-0 w-9 h-9 rounded-full items-center justify-center bg-force-surface-2/70 border border-force-border-light text-force-green-light hover:border-force-green-light/50 transition-colors"
      >
        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
      <div
        ref={scrollerRef}
        className="flex-1 flex gap-6 overflow-x-auto scroll-smooth snap-x snap-mandatory py-2 px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {items.map((p) => (
          <div key={p.name} className="shrink-0 snap-start flex flex-col items-center gap-2 w-24">
            <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-force-green-light/40 bg-force-surface-2">
              <img src={p.photo} alt={p.name} className="w-full h-full object-cover" />
            </div>
            <p className="font-caption text-caption text-force-text-muted text-center leading-tight">{p.name}</p>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => scrollByCards(1)}
        aria-label="Scroll recommendations right"
        className="hidden sm:flex shrink-0 w-9 h-9 rounded-full items-center justify-center bg-force-surface-2/70 border border-force-border-light text-force-green-light hover:border-force-green-light/50 transition-colors"
      >
        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
    </div>
  )
}

export default function Landing() {
  return (
    <div className="flex flex-col min-h-screen">
      <ForceHeader />

      <main className="flex-grow">
        <section className="relative bg-force-bg overflow-hidden md:min-h-[760px]">
          <div
            className="absolute inset-0 opacity-40"
            style={{ backgroundImage: 'url(/assets/topo-pattern.svg)', backgroundSize: 'cover', backgroundPosition: 'center' }}
          />
          <picture>
            <source srcSet="/assets/hero-soldier.webp" type="image/webp" />
            <img
              src="/assets/hero-soldier.jpg"
              alt=""
              aria-hidden="true"
              width={1800}
              height={1012}
              fetchPriority="high"
              className="absolute inset-0 w-full h-full object-cover object-[78%_center]"
            />
          </picture>
          {/* Mobile stacks text directly over the photo (no side-by-side room for the
              desktop fade below), so it needs its own full-width scrim: darkest up top
              where the headline/paragraph sit, easing off where the solid-color buttons
              (which don't need backdrop contrast) cover the photo, then back to fully
              opaque #0a0b08 at the very bottom, the next section's image was pre-blended
              to that exact flat color, so the seam needs to land on solid color here too,
              not mid-photo, or that work shows up as a hard cut instead of a fade. */}
          <div
            className="md:hidden absolute inset-0"
            style={{
              background:
                'linear-gradient(180deg, rgba(10,11,8,0.9) 0%, rgba(10,11,8,0.7) 30%, rgba(10,11,8,0.4) 50%, rgba(10,11,8,0.25) 70%, #0a0b08 100%)',
            }}
          />
          <div
            className="hidden md:block absolute inset-0"
            style={{ background: 'linear-gradient(90deg, #0a0b08 0%, rgba(10,11,8,0.85) 22%, rgba(10,11,8,0.35) 36%, rgba(10,11,8,0.08) 50%, rgba(10,11,8,0) 68%, rgba(10,11,8,0) 100%)' }}
          />
          <div
            className="hidden md:block absolute inset-0"
            style={{ background: 'linear-gradient(180deg, transparent 70%, #0a0b08 100%)' }}
          />
          <div
            className="hidden md:block absolute inset-0"
            style={{ background: 'linear-gradient(90deg, transparent calc(100% - 340px), #0a0b08 100%)' }}
          />
          <div className="relative z-10 max-w-container-max mx-auto md:min-h-[760px]">
            <div
              className="grid grid-cols-1 md:grid-cols-12 gap-gutter items-center px-margin-mobile md:px-margin-desktop py-margin-desktop md:min-h-[760px]"
            >
              <div className="md:col-span-6 flex flex-col gap-stack-md">
                <div className="flex flex-col items-start gap-3">
                  <h1 className="font-display-lg text-headline-lg-mobile md:text-display-lg text-force-text m-0">
                    <span className="text-force-text">Force </span>
                    <span className="text-force-green-light">AI</span>
                  </h1>
                  <span className="inline-block border border-force-green-light/50 rounded-md px-3 py-1.5 font-caption text-caption uppercase tracking-[0.25em] text-force-text">
                    Military Leadership Made Easy
                  </span>
                </div>
                <p className="font-body-lg text-body-lg text-force-text max-w-sm">
                  India's first AI platform built to make you think like a military leader.
                </p>
                <p className="font-caption text-caption text-force-text-muted">
                  Scoring calibrated with input from officers and psychologists.
                </p>
                <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 pt-2">
                  <Link
                    to="/login?next=/app"
                    className="group relative w-full sm:w-auto flex items-center justify-center gap-3 bg-[#7c9159] text-[#16220c] border border-[#9db075] rounded-xl px-5 py-2.5 overflow-hidden transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_22px_rgba(92,122,63,0.45)]"
                  >
                    <span
                      aria-hidden="true"
                      className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out bg-gradient-to-r from-transparent via-white/25 to-transparent -skew-x-12"
                    />
                    <WreathBadge icon="target" ring="#1f3010" leaf="#30421d" bg="#8fa468" />
                    <span className="relative w-px self-stretch bg-[#16220c]/25" />
                    <span className="relative font-label-md text-body-md font-bold tracking-wide">SSB Module</span>
                    <span className="material-symbols-outlined relative text-xl">chevron_right</span>
                  </Link>
                  <Link
                    to="/login?next=/app/bsf"
                    className="group relative w-full sm:w-auto flex items-center justify-center gap-3 bg-[#9c8a5e] text-[#2a2410] border border-[#c3b081] rounded-xl px-5 py-2.5 overflow-hidden transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#ab9a6e] hover:shadow-[0_8px_22px_rgba(156,138,94,0.5)]"
                  >
                    <span
                      aria-hidden="true"
                      className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out bg-gradient-to-r from-transparent via-white/35 to-transparent -skew-x-12"
                    />
                    <WreathBadge icon="military_tech" ring="#4a3c17" leaf="#5c4a1f" bg="#c3ad78" />
                    <span className="relative w-px self-stretch bg-[#2a2410]/25" />
                    <span className="relative font-label-md text-body-md font-bold tracking-wide">BSF Module</span>
                    <span className="material-symbols-outlined relative text-xl">chevron_right</span>
                  </Link>
                </div>
                <div className="pt-2">
                  <AskAnythingBar variant="hero" />
                </div>
              </div>
              <div className="hidden md:block md:col-span-6">
                <ForceHeroCard />
              </div>
            </div>
          </div>
        </section>

        <div
          className="relative z-0"
          style={{
            background:
              'linear-gradient(180deg, #0a0b08 0%, #0c0d0a 18%, #10120c 36%, #171910 54%, #1f2216 74%, #242a1a 90%, #242a1a 100%)',
          }}
        >
          <div
            className="absolute inset-0 -z-10 pointer-events-none opacity-40"
            style={{
              backgroundImage: 'url(/assets/topo-pattern.svg)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              maskImage: 'linear-gradient(180deg, transparent 0%, black 35%)',
              WebkitMaskImage: 'linear-gradient(180deg, transparent 0%, black 35%)',
            }}
            aria-hidden="true"
          />
          <img
            src="/assets/academies-path.webp"
            alt="The Army, Naval and Air Force academies connected by a glowing path"
            width={1573}
            height={1000}
            loading="lazy"
            className="block w-full h-auto md:max-h-[760px] object-cover object-center"
            style={{
              maskImage: 'linear-gradient(180deg, transparent 0%, black 7%, black 86%, transparent 100%)',
              WebkitMaskImage: 'linear-gradient(180deg, transparent 0%, black 7%, black 86%, transparent 100%)',
            }}
          />
        </div>

        <div className="relative z-0 overflow-hidden" style={{ background: '#10120c' }}>
          <img
            src="/assets/misty-mountains.png"
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div
            className="absolute inset-0 -z-0 pointer-events-none"
            style={{
              background:
                'linear-gradient(180deg, #242a1a 0%, rgba(16,18,11,0.55) 8%, rgba(10,12,8,0.68) 40%, rgba(10,12,8,0.74) 70%, rgba(15,17,11,0.88) 92%, #242a1a 100%)',
            }}
          />
          <div
            className="absolute inset-0 -z-0 pointer-events-none opacity-25 mix-blend-overlay"
            style={{ backgroundImage: 'url(/assets/topo-pattern.svg)', backgroundSize: 'cover', backgroundPosition: 'center' }}
            aria-hidden="true"
          />

          <section id="how-it-works" className="relative py-margin-desktop px-margin-mobile md:px-margin-desktop max-w-container-max mx-auto">
            <h2 className="font-headline-lg-mobile text-headline-lg-mobile md:font-headline-lg md:text-headline-lg text-force-text text-center mb-stack-lg">
              How It Works
            </h2>
            <div className="relative">
              <div className="hidden md:block absolute top-7 left-[12.5%] right-[12.5%] h-px bg-force-border-light" aria-hidden="true" />
              <div className="md:hidden absolute top-7 bottom-7 left-9 w-px bg-force-border-light" aria-hidden="true" />
              <div className="grid grid-cols-1 md:grid-cols-4 gap-y-10 gap-x-gutter">
                {HOW_IT_WORKS.map((s) => (
                  <div key={s.step} className="relative z-10 flex flex-row md:flex-col items-start md:items-center text-left md:text-center gap-4 md:gap-3 px-2">
                    <div className="w-14 h-14 shrink-0 rounded-full border-2 border-force-green-light/70 bg-force-bg/40 backdrop-blur-sm flex items-center justify-center font-mono text-lg font-bold text-force-green-light">
                      {s.step}
                    </div>
                    <div className="flex flex-col gap-1 md:gap-3 md:items-center pt-2 md:pt-0">
                      <h3 className="font-title-md text-title-md text-force-text">{s.title}</h3>
                      <p className="font-body-md text-body-md text-force-text-muted max-w-[240px]">{s.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="relative py-margin-desktop px-margin-mobile md:px-margin-desktop max-w-container-max mx-auto">
            <h2 className="font-headline-lg-mobile text-headline-lg-mobile md:font-headline-lg md:text-headline-lg text-force-text text-center mb-stack-lg">
              Features
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-gutter">
              {FEATURES.map((f) => (
                <div key={f.title} className="relative bg-force-surface-2/55 backdrop-blur-sm rounded-lg p-6 border border-force-border-light hover:border-force-green-light/40 hover:shadow-[0_4px_24px_rgba(0,0,0,0.35)] transition-all overflow-hidden">
                  <CardBrackets />
                  <div className="relative z-10">
                    <div className="w-12 h-12 rounded-lg bg-force-green-light/15 text-force-green-light flex items-center justify-center mb-4">
                      <span className="material-symbols-outlined">{f.icon}</span>
                    </div>
                    <h3 className="font-title-md text-title-md text-force-text mb-2">{f.title}</h3>
                    <p className="font-body-md text-body-md text-force-text-muted">{f.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section id="ai-in-action" className="relative py-margin-desktop px-margin-mobile md:px-margin-desktop max-w-container-max mx-auto">
            <div className="text-center mb-stack-lg max-w-2xl mx-auto">
              <h2 className="font-headline-lg-mobile text-headline-lg-mobile md:font-headline-lg md:text-headline-lg text-force-text mb-2">
                AI in Action
              </h2>
              <p className="font-body-md text-body-md text-force-text-muted">
                Real product mechanics, not a generic dashboard screenshot.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-gutter">
              <div className="relative rounded-lg border border-force-border-light bg-force-surface-2/55 backdrop-blur-sm p-5 overflow-hidden">
                <CardBrackets />
                <div className="relative z-10 flex flex-col gap-3">
                  <p className="font-mono text-[10px] tracking-[0.15em] text-force-text-muted uppercase">TAT Analysis</p>
                  <div className="h-28 rounded-lg bg-white/5 flex items-center justify-center text-force-text-muted/50">
                    <span className="material-symbols-outlined text-3xl">image</span>
                  </div>
                  <div className="flex items-center justify-between font-mono text-[10px] tracking-[0.1em] text-force-text-muted uppercase">
                    <span>Score</span>
                    <span className="font-bold text-force-green-light normal-case tracking-normal">8.5/10</span>
                  </div>
                  <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-force-green-light rounded-full" style={{ width: '85%' }} />
                  </div>
                </div>
              </div>
              <div className="relative rounded-lg border border-force-border-light bg-force-surface-2/55 backdrop-blur-sm p-5 overflow-hidden">
                <CardBrackets />
                <div className="relative z-10 flex flex-col gap-3 h-full">
                  <p className="font-mono text-[10px] tracking-[0.15em] text-force-text-muted uppercase">AI Interview</p>
                  <div className="flex flex-col gap-2 font-body-md text-body-md">
                    <p className="text-force-text-muted">Why do you want to join the armed forces?</p>
                    <p className="bg-force-green-light/15 text-force-text rounded-lg px-3 py-2 self-end">
                      I want to serve the nation and challenge myself.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 mt-auto">
                    <span className="material-symbols-outlined text-force-text-muted">graphic_eq</span>
                    <span className="font-caption text-caption text-force-text-muted">AI Feedback available</span>
                  </div>
                </div>
              </div>
              <div className="relative rounded-lg border border-force-border-light bg-force-surface-2/55 backdrop-blur-sm p-5 overflow-hidden">
                <CardBrackets />
                <div className="relative z-10 flex flex-col gap-3">
                  <p className="font-mono text-[10px] tracking-[0.15em] text-force-text-muted uppercase">OLQ Breakdown</p>
                  <div className="flex flex-col gap-2">
                    {['Planning & Organising', 'Social Adjustment', 'Social Effectiveness', 'Dynamic'].map((k, i) => (
                      <div key={k} className="flex items-center justify-between gap-2 font-caption text-caption text-force-text-muted">
                        <span>{k}</span>
                        <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden mx-2">
                          <div className="h-full bg-force-green-light rounded-full" style={{ width: `${70 + i * 5}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section id="advantage" className="relative py-margin-desktop px-margin-mobile md:px-margin-desktop max-w-container-max mx-auto">
            <div className="rounded-2xl border border-force-green-light/25 bg-force-surface-2/50 backdrop-blur-sm p-6 md:p-10 max-w-3xl mx-auto">
              <div className="flex justify-center mb-stack-lg">
                <span className="bg-force-green text-force-text font-label-md text-label-md px-4 py-2 rounded-full">
                  Why Force AI is Better for You
                </span>
              </div>
              <div className="flex flex-col gap-6">
                {WHY_FORCE_AI.map((w) => (
                  <div key={w.title} className="flex items-start gap-4">
                    <div className="w-11 h-11 shrink-0 rounded-full bg-force-green-light/15 text-force-green-light flex items-center justify-center">
                      <span className="material-symbols-outlined">{w.icon}</span>
                    </div>
                    <div>
                      <h3 className="font-title-md text-title-md text-force-text">{w.title}</h3>
                      <p className="font-body-md text-body-md text-force-text-muted">{w.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="recommended-by" className="relative py-margin-desktop px-margin-mobile md:px-margin-desktop max-w-container-max mx-auto">
            <div className="flex flex-col items-center text-center mb-stack-lg">
              <span className="inline-flex items-center gap-2 bg-force-surface-2/60 backdrop-blur-sm border border-force-border-light rounded-full px-4 py-1.5 mb-3">
                <span className="material-symbols-outlined text-force-green-light text-base">groups</span>
                <span className="font-label-md text-label-md text-force-text uppercase tracking-wide">Force DVS Recommendation</span>
              </span>
              <p className="font-body-md text-body-md text-force-text-muted max-w-xl">
                Real candidates, recently recommended after training with Force AI.
              </p>
            </div>
            <RecommendationCarousel items={RECOMMENDED_CANDIDATES} />
          </section>
        </div>

        <div className="relative z-0" style={{ background: '#242a1a' }}>
        <section id="cta" className="relative overflow-hidden">
          <img
            src="/assets/cta-bg.png"
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover object-[25%_center] opacity-70 -scale-x-100"
          />
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(90deg, #242a1a 0%, #242a1a 25%, rgba(36,42,26,0.55) 65%, rgba(36,42,26,0.35) 100%)' }}
          />
          <div className="relative flex flex-col md:flex-row items-center justify-between gap-6 py-margin-desktop px-margin-mobile md:px-margin-desktop max-w-container-max mx-auto">
            <div>
              <p className="font-headline-lg-mobile text-headline-lg-mobile text-force-text">
                You don't need luck in SSB.
              </p>
              <p className="font-headline-lg-mobile text-headline-lg-mobile text-force-green-light">You need clarity.</p>
              <p className="hidden md:block font-body-md text-body-md text-force-text-muted mt-2">
                Let Force AI be your guide. Start your journey today.
              </p>
            </div>
            <div className="relative shrink-0">
              <CornerBracket className="-top-2 -left-2 border-t border-l" />
              <CornerBracket className="-bottom-2 -right-2 border-b border-r" />
              <Link
                to="/login"
                className="bg-force-green text-force-text font-label-md text-label-md px-6 py-3 rounded-lg hover:bg-force-green-light transition-colors whitespace-nowrap"
              >
                Start Assessment →
              </Link>
            </div>
          </div>
        </section>
        </div>
      </main>

      <ForceFooter />
    </div>
  )
}
